import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import PayRollLayoutWrapper from "../components/PayRollLayoutWrapper";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Bell, Newspaper, RefreshCcw } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const PayrollHome = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId");
  const branchId = localStorage.getItem("branchId");
  const branchType = localStorage.getItem("branchType") || "HO";

  const screenBadge = useMemo(() => "INFRA SCREEN : PAYROLL HOME", []);

  const [loading, setLoading] = useState(false);
  const [news, setNews] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const fetchAll = async () => {
    if (!finYearId) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!branchId || !branchType) {
      toast.warn("Missing login branch information.");
      return;
    }

    try {
      setLoading(true);

      const newsReq = axios.get(`${BASE_URL}/api/payroll-home/news`, {
        params: { finYearId: Number(finYearId) },
      });

      const alertReq = axios.get(`${BASE_URL}/api/payroll-home/renewal-alert`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType,
        },
      });

      const [nres, ares] = await Promise.allSettled([newsReq, alertReq]);

      if (nres.status === "fulfilled" && nres.value?.data?.success) {
        setNews(nres.value.data.rows || []);
      } else {
        setNews([]);
      }

      if (ares.status === "fulfilled" && ares.value?.data?.success) {
        setAlerts(ares.value.data.rows || []);
      } else {
        setAlerts([]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load payroll home.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNews = (newsId) => {
    window.location.href = `/payroll/view-news?newsId=${encodeURIComponent(newsId)}`;
  };

  const openAlertPopup = (employeeId) => {
    window.open(
      `/PayRoll/LicenseDateUpdate?alertId=${encodeURIComponent(employeeId)}`,
      "_blank",
      "top=200,left=250,scrollbars=yes,width=450,height=330"
    );
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PAYROLL HOME</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {alerts.length} Alerts
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-amber-900/40 text-amber-100" : "bg-amber-100 text-amber-800"
                }`}
              >
                {news.length} News
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={fetchAll} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={() => toast.info("Home loaded")} className={primaryBtn}>
                <Bell className="w-4 h-4" />
                Dashboard
              </button>
            </div>
          </div>

          {/* ✅ FLASH NEWS FULL WIDTH (TOP ROW) */}
          <div className={cardBase + " p-5 mb-6"}>
            <div className="flex items-center gap-3 mb-4">
              <Newspaper className={isDark ? "text-white" : "text-gray-700"} />
              <div className={`text-xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                Flash News
              </div>
            </div>

            <div className="max-h-[240px] overflow-y-auto space-y-4">
              {news.length === 0 ? (
                <div className={`${isDark ? "text-gray-300" : "text-gray-700"} text-center py-12 opacity-70`}>
                  No News Found
                </div>
              ) : (
                news.map((n, i) => (
                  <div key={i} className={`p-3 rounded-2xl ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                    <div className="text-xs opacity-70 mb-1">#{n.fld_newsid}</div>

                    <button
                      type="button"
                      onClick={() => openNews(n.fld_newsid)}
                      className={`text-left w-full font-extrabold ${
                        isDark ? "text-amber-200" : "text-amber-900"
                      } hover:underline`}
                    >
                      {n.fld_Heading}
                    </button>

                    <div className={`text-sm mt-1 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                      {n.fld_shortdescription}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ✅ RENEWAL ALERT TABLE FULL WIDTH (BOTTOM ROW) */}
          <div className={cardBase + " p-5"}>
            <div className="flex items-center gap-3 mb-4">
              <Bell className={isDark ? "text-white" : "text-gray-700"} />
              <div className={`text-xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                Renewal Alert
              </div>
            </div>

            <div className="overflow-auto max-h-[520px]">
              <table className="w-full text-sm">
                <thead className={`${primaryBtnClass} text-white sticky top-0`}>
                  <tr>
                    <th className="px-3 py-3 text-center font-bold">S.No</th>
                    <th className="px-3 py-3 text-center font-bold">Employee</th>
                    <th className="px-3 py-3 text-center font-bold">Designation</th>
                    <th className="px-3 py-3 text-center font-bold">Branch</th>
                    <th className="px-3 py-3 text-center font-bold">Expiry Date</th>
                    <th className="px-3 py-3 text-center font-bold">Alert</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 10 }).map((_, i) => (
                      <tr key={i} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 6 }).map((__, c) => (
                          <td key={c} className="px-3 py-3">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : alerts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={`px-4 py-14 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    alerts.map((a, i) => (
                      <tr
                        key={i}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                      >
                        <td className={`px-3 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {i + 1}
                        </td>

                        <td className="px-3 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => openAlertPopup(a.fld_Employeeid)}
                            className={`font-extrabold hover:underline ${
                              isDark ? "text-amber-200" : "text-amber-900"
                            }`}
                          >
                            {a.fld_Employee}
                          </button>
                        </td>

                        <td className={`px-3 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {a.fld_designationname}
                        </td>

                        <td className={`px-3 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {a.fld_BranchName}
                        </td>

                        <td className="px-3 py-3 text-center font-extrabold text-red-600">
                          {a.Lincenseexpdate}
                        </td>

                        <td className="px-3 py-3 text-center font-extrabold text-red-600">
                          {a.Alert}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3 text-xs opacity-70">
              *Expiry Date / Alert fields are shown in red.
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default PayrollHome;